import {
  computed,
  ref,
  watch,
  onBeforeUnmount,
  useHost,
  type ComputedRef,
  type Ref,
  type ToRefs,
  type WritableComputedRef,
} from "vue";
import type { MenuItemProps, FloatingChild } from "./menu.types";
import { useSharedState } from "composables/useSharedState";
import { useLayout } from "components/Layout/useLayout";
import { usePersistentRef } from "composables/usePersistentRef";
import { createEventDispatcher } from "@/runtime/shared/events";
import { menuItemClasses } from "./menu.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { getCurrentPath, matchesRoute } from "./useMenu";
import { useMenuCoordination } from "./useMenu.Coordination";
import type { UseMenuCoordinationReturn } from "./useMenu.Coordination";
import { useTypeahead } from "composables/useTypeahead";
import { useElementHover } from "@vueuse/core";
import { getDeepActiveElement, findMenuItemHost, getSubItems, handleListNavigation } from "./useMenuKeyboard";

const UNSAFE_URL_RE = /^(?:javascript|data|vbscript):/i;

const sanitizeHref = (url: string | undefined): string | undefined => {
  if (!url) return undefined;
  const trimmed = url.trim();
  return UNSAFE_URL_RE.test(trimmed) ? undefined : trimmed;
};

/** Build floating children from props.children array. */
const buildChildrenFromProps = (children: MenuItemProps["children"]): FloatingChild[] => {
  if (!children?.length) return [];
  const result: FloatingChild[] = [];
  for (const child of children) {
    if (child.label) {
      result.push({
        label: child.label,
        to: sanitizeHref(child.to),
        icon: child.icon,
        target: child.target ?? undefined,
      });
    }
  }
  return result;
};

/** Build floating children from slotted DOM elements. */
const buildChildrenFromSlots = (host: Element): FloatingChild[] => {
  const result: FloatingChild[] = [];
  for (const el of host.querySelectorAll('[slot="children"]')) {
    const label = el.getAttribute("label") || el.textContent?.trim();
    if (label) {
      result.push({
        label,
        to: sanitizeHref(el.getAttribute("to") ?? undefined),
        icon: el.getAttribute("icon") ?? undefined,
        target: el.getAttribute("target") ?? undefined,
      });
    }
  }
  return result;
};

/** Build floating children list from props.children or slotted DOM children. */
const buildFloatingChildren = (props: Readonly<MenuItemProps>, host: Element | null): FloatingChild[] => {
  if (props.children?.length) return buildChildrenFromProps(props.children);
  return host ? buildChildrenFromSlots(host) : [];
};

/** Read the label of a host element, falling back to its direct text content. */
const getHostLabel = (props: Readonly<MenuItemProps>, host: Element | null): string => {
  if (props.label) return props.label;
  if (!host) return "";
  let text = "";
  for (const node of host.childNodes) {
    if (node.nodeType === Node.TEXT_NODE || (node instanceof Element && !node.hasAttribute("slot"))) {
      text += node.textContent;
    }
  }
  return text.trim();
};

/** Key-action lookup for sub-item keyboard navigation. */
type KeyAction = (ctx: {
  event: KeyboardEvent;
  openedProxy: { value: boolean };
  host: Element;
  subItems: HTMLElement[];
  focusedHost: HTMLElement;
  typeahead: ReturnType<typeof useTypeahead>;
}) => boolean;

const SUB_ITEM_KEY_ACTIONS: Record<string, KeyAction> = {
  Escape: ({ event, openedProxy, host }) => {
    event.preventDefault();
    event.stopPropagation();
    openedProxy.value = false;
    (host as HTMLElement).shadowRoot?.querySelector("summary")?.focus();
    return true;
  },
  Enter: ({ event, openedProxy }) => {
    event.stopPropagation();
    queueMicrotask(() => {
      openedProxy.value = true;
    });
    return true;
  },
  ArrowUp: ({ event, subItems, focusedHost, host }) => {
    if (subItems.indexOf(focusedHost) === 0) {
      event.preventDefault();
      event.stopPropagation();
      (host as HTMLElement).shadowRoot?.querySelector("summary")?.focus();
      return true;
    }
    return false;
  },
  ArrowDown: ({ subItems, focusedHost }) => {
    // On last sub-item, let event bubble to menu-level handler
    return subItems.indexOf(focusedHost) === subItems.length - 1;
  },
};

/** Handle keydown events within expanded menu item details. */
const handleItemKeydownEvent = (
  e: Event,
  menu: UseMenuCoordinationReturn,
  openedProxy: { value: boolean },
  host: Element,
  typeahead: ReturnType<typeof useTypeahead>,
): void => {
  const event = e as KeyboardEvent;
  if (!menu.isExpandable.value || !openedProxy.value) return;

  const deepActive = getDeepActiveElement();
  const focusedHost = deepActive ? findMenuItemHost(deepActive) : null;
  if (!focusedHost || focusedHost === host) return;

  const subItems = getSubItems(host as HTMLElement);
  if (!subItems.includes(focusedHost)) return;

  const action = SUB_ITEM_KEY_ACTIONS[event.key];
  if (action) {
    const handled = action({ event, openedProxy, host, subItems, focusedHost, typeahead });
    if (handled) return;
  }

  if (handleListNavigation(event, subItems, focusedHost, typeahead)) {
    event.stopPropagation();
  }
};

export interface UseMenuItemOptions {
  props: Readonly<MenuItemProps>;
  triggerRef?: Ref<HTMLElement | null>;
}

export interface UseMenuItemReturn {
  state: ToRefs<MenuItemProps>;
  classes: ComputedRef<ReturnType<typeof menuItemClasses>>;
  openedProxy: WritableComputedRef<boolean>;
  isCollapsed: ComputedRef<boolean>;
  isChild: ComputedRef<boolean>;
  isExpandable: ComputedRef<boolean>;
  hasChildren: Ref<boolean>;
  showFloating: ComputedRef<boolean>;
  isActive: ComputedRef<boolean>;
  isExternalTarget: ComputedRef<boolean>;
  externalRel: ComputedRef<"noopener noreferrer" | undefined>;
  detailsTrailingIconName: ComputedRef<"open-in-new" | "chevron-right" | undefined>;
}

/** Build the opened proxy and persist state. */
const buildOpenedProxy = (
  state: ToRefs<MenuItemProps>,
  props: Readonly<MenuItemProps>,
  host: Element | null,
  dispatchEvent: ReturnType<typeof createEventDispatcher>,
  menu: UseMenuCoordinationReturn,
  layoutCollapsed: ComputedRef<boolean>,
  toggleCollapsed: () => void,
): WritableComputedRef<boolean> => {
  if (state.open && host) {
    usePersistentRef(state.open, {
      key: "e-menu-item:open",
      scope: "position",
      options: { host: host as HTMLElement, props: { label: props.label, to: props.to, icon: props.icon } },
    });
  }
  return computed({
    get: () => state.open?.value ?? props.open ?? false,
    set: (v: boolean) => {
      dispatchEvent("update:open", v);
      if (v) {
        menu.open();
        menu.closeOthers();
        if (layoutCollapsed.value) toggleCollapsed();
      } else menu.close();
    },
  });
};

/** Build route-based active detection and auto-open watchers. */
const buildIsActive = (
  props: Readonly<MenuItemProps>,
  host: Element | null,
  menu: UseMenuCoordinationReturn,
  isCollapsed: ComputedRef<boolean>,
  openedProxy: WritableComputedRef<boolean>,
): ComputedRef<boolean> => {
  const currentPath = getCurrentPath();
  const slottedChildRoutes: string[] = [];
  if (!props.to && host) {
    for (const el of host.querySelectorAll('[slot="children"]')) {
      const childTo = el.getAttribute("to");
      if (childTo) slottedChildRoutes.push(childTo);
    }
  }
  const isActive = computed(() => {
    const path = currentPath.value;
    if (props.to) return matchesRoute(props.to, path);
    if (props.children?.length) return props.children.some((c) => matchesRoute(c.to, path));
    return slottedChildRoutes.some((to) => matchesRoute(to, path));
  });
  watch(currentPath, () => {
    if (isActive.value && menu.isExpandable.value && !isCollapsed.value) openedProxy.value = true;
  });
  watch(isCollapsed, (collapsed) => {
    if (!collapsed && isActive.value && menu.isExpandable.value) openedProxy.value = true;
  });
  return isActive;
};

/** Build the menu-item classes computed. */
const buildMenuItemClasses = (
  props: Readonly<MenuItemProps>,
  isActive: ComputedRef<boolean>,
  menu: UseMenuCoordinationReturn,
  openedProxy: WritableComputedRef<boolean>,
  isCollapsed: ComputedRef<boolean>,
): ComputedRef<ReturnType<typeof menuItemClasses>> =>
  computed(() => {
    const isSelected = props.to ? isActive.value : !!props.selected;
    return mergeClassProp(
      menuItemClasses({
        type: props.type,
        active: isActive.value ? "true" : "false",
        selected: isSelected ? "true" : "false",
        disabled: props.disabled ? "true" : "false",
        isSubItem: menu.isChild.value ? "true" : "false",
        detailsOpen: openedProxy.value ? "true" : "false",
        collapsed: isCollapsed.value ? "true" : "false",
      }),
      props.class,
    );
  });

/** Build the three target/trailing-icon computeds. */
const buildTargetComputeds = (
  state: ToRefs<MenuItemProps>,
  isCollapsed: ComputedRef<boolean>,
): Pick<UseMenuItemReturn, "isExternalTarget" | "externalRel" | "detailsTrailingIconName"> => {
  const isExternalTarget = computed(() => state.target?.value === "_blank");
  const externalRel = computed<"noopener noreferrer" | undefined>(() =>
    isExternalTarget.value ? "noopener noreferrer" : undefined,
  );
  const detailsTrailingIconName = computed<"open-in-new" | "chevron-right" | undefined>(() => {
    if (isCollapsed.value || state.type?.value === "label") return;
    return isExternalTarget.value ? "open-in-new" : "chevron-right";
  });
  return { isExternalTarget, externalRel, detailsTrailingIconName };
};

/** Attach keydown listener to host and wire cleanup. */
const setupMenuItemKeydown = (
  host: Element,
  menu: UseMenuCoordinationReturn,
  openedProxy: WritableComputedRef<boolean>,
): void => {
  const typeahead = useTypeahead();
  const handleItemKeydown = (e: Event) => handleItemKeydownEvent(e, menu, openedProxy, host, typeahead);
  host.addEventListener("keydown", handleItemKeydown);
  onBeforeUnmount(() => {
    host.removeEventListener("keydown", handleItemKeydown);
    typeahead.resetTypeahead();
  });
};

/** Set up hover-based floating dispatch. */
const setupFloatingHover = (
  props: Readonly<MenuItemProps>,
  host: Element | null,
  triggerRef: Ref<HTMLElement | null> | undefined,
  showFloating: ComputedRef<boolean>,
  dispatchEvent: ReturnType<typeof createEventDispatcher>,
): void => {
  const nullRef = ref<EventTarget | null>(null);
  const isTriggerHovered = useElementHover(triggerRef ?? nullRef);
  watch(isTriggerHovered, (hovered) => {
    if (!showFloating.value || !host || !triggerRef?.value) return;
    if (hovered) {
      dispatchEvent("floating:show", {
        label: getHostLabel(props, host),
        children: buildFloatingChildren(props, host),
        triggerElement: triggerRef.value,
      });
    } else {
      dispatchEvent("floating:hide", { triggerElement: triggerRef.value });
    }
  });
  watch(showFloating, (show) => {
    if (!show && host && triggerRef?.value) dispatchEvent("floating:hide", { triggerElement: triggerRef.value });
  });
};

export const useMenuItem = (options: UseMenuItemOptions): UseMenuItemReturn => {
  const { props, triggerRef } = options;
  const host = useHost();
  const state = useSharedState(props);
  const dispatchEvent = createEventDispatcher(host);
  const { toggleCollapsed, collapsed: layoutCollapsed } = useLayout();
  const menu = useMenuCoordination();

  const openedProxy = buildOpenedProxy(state, props, host, dispatchEvent, menu, layoutCollapsed, toggleCollapsed);
  const isCollapsed = computed(() => state.collapsed?.value || layoutCollapsed?.value);
  const isActive = buildIsActive(props, host, menu, isCollapsed, openedProxy);
  const classes = buildMenuItemClasses(props, isActive, menu, openedProxy, isCollapsed);

  const showFloating = computed(() => isCollapsed.value && menu.currentLevel.value === 0 && props.type !== "label");
  const { isExternalTarget, externalRel, detailsTrailingIconName } = buildTargetComputeds(state, isCollapsed);
  setupFloatingHover(props, host, triggerRef, showFloating, dispatchEvent);

  if (host) setupMenuItemKeydown(host, menu, openedProxy);

  return {
    state,
    classes,
    openedProxy,
    isCollapsed,
    isChild: menu.isChild,
    isExpandable: menu.isExpandable,
    hasChildren: menu.hasChildren,
    showFloating,
    isActive,
    isExternalTarget,
    externalRel,
    detailsTrailingIconName,
  };
};
